import { Text, TextInput } from '@mantine/core';
import React, { useContext, useRef, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useRouter } from "next/navigation";
import { useFirebaseRefs } from "../../lib/firebase";
import { Guide } from '../../lib/interfaces';
import { SheetsData } from "../Sheets";
import { blurOnEnterKey } from "../../lib/eventHandlers";
import { StepWizard } from "../StepWizard/StepWizard";
import { ClearTextAreaRightSection } from "../AddEditTrip/ClearTextAreaRightSection";
import { useKeyboardScrollEffect } from '../../lib/keyboard';

import stepWizardClasses from "../StepWizard/StepWizard.module.css";
import { deleteGuide } from '../../lib/guideFunctions';
import { UserContext } from '../../lib/context';
import { GUIDES_TAB_NAME } from '../TravelScrollMenu';

/**
 * Shows a series of modal sheets collecting information to delete a guide
 */
export async function showDeleteGuide(sheets: SheetsData, guideTemplate: Partial<Guide>) {
    const title = `Delete Guide`;

    sheets.showSheetWith(
        context => <ConfirmDeleteGuide onClose={() => context.removeSheet()} guide={guideTemplate} />,
        {
            title,
            disableDrag: true,
        }
    );
}

type DeleteGuideForm = UseFormReturnType<{
    title: string;
},
    (values: {
        title: string;
    }) => {
        title: string;
    }>;

function SectionOneDeleteGuide(props: {
    innerRef: React.RefObject<HTMLInputElement>,
    guide: Partial<Guide>,
    form: DeleteGuideForm,
    onInput: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onClear: () => void }) {
    return <div
        className={stepWizardClasses.stepSection}
        aria-label='Guide Deletion Confirmation'
    >
        <Text style={{ textAlign: 'center' }}>Confirm you want to delete this guide by typing the title: {props.guide.title ?? ''}</Text>
        <div
            className={stepWizardClasses.progress}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <TextInput
                onFocus={() => props.innerRef.current?.scrollIntoView()}
                ref={props.innerRef}
                py="sm"
                required
                autoFocus
                placeholder={props.guide.title ?? ''}
                label="Guide Title:"
                style={{ display: "inline-block", flex: 1 }}
                {...props.form.getInputProps("title")}
                onInput={props.onInput}
                enterKeyHint="done"
                onKeyUp={blurOnEnterKey}
                rightSection={<ClearTextAreaRightSection onClear={props.onClear} />}
            />
        </div>
    </div>;
}

export function ConfirmDeleteGuide(
    {
        guide,
        onClose,
    }: {
        guide: Partial<Guide>,
        onClose: () => unknown,
    }) {
    const firebaseRefs = useFirebaseRefs();
    const user = useContext(UserContext);

    const inputTextRef = useRef<HTMLInputElement>(null);
    useKeyboardScrollEffect(inputTextRef);

    const form = useForm({
        initialValues: {
            title: '',
        },

        validate: {
            title: (value: string) => (value === guide.title ? null : 'Your title does not match the guide'),
        },
    });
    const nextStep = () => 0;
    const [ currentStep, setCurrentStep ] = useState(0);

    const steps = [ '' ];
    const router = useRouter();

    return <StepWizard
        updateSection='Delete'
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        nextStep={nextStep}
        onDone={async () => {
            if (form.validate().hasErrors) {
                return setCurrentStep((current) => current);
            } else {
                const title = form.values.title ?? '';
                const detail = title || '';
                deleteGuide(firebaseRefs, guide, user);
                form.reset();
                router.push(`/?tab=${GUIDES_TAB_NAME}`);
                onClose();
            }
        }}
    >

        { currentStep === 0 &&
            <SectionOneDeleteGuide
                innerRef={inputTextRef}
                guide={guide}
                form={form}
                onInput={e => form.setFieldValue('title', e.currentTarget.value)}
                onClear={() => form.setFieldValue('title', '')} />}
    </StepWizard>;
}
